import React, { useState } from 'react';
import { theme } from '../styles/theme';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { Input } from '../components/Input';
import { Textarea } from '../components/Textarea';
import { FileUpload } from '../components/FileUpload';

export const AgencyProfileEditPage = () => {
  const [agencyName, setAgencyName] = useState('Sterling Literary Agency');
  const [tagline, setTagline] = useState('Representing exceptional voices in contemporary literature');
  const [about, setAbout] = useState(
    `Sterling Literary Agency was founded in 2015 with a mission to discover and champion exceptional literary voices. We represent a diverse roster of authors across multiple genres, with a particular focus on literary fiction, commercial fiction, and narrative nonfiction.\n\nOur agents bring decades of combined experience in publishing, with strong relationships at major publishing houses and a track record of successful deals. We pride ourselves on our hands-on editorial approach and commitment to long-term author career development.\n\nWe are always seeking fresh, compelling voices and stories that challenge, inspire, and entertain.`
  );
  const [foundedYear, setFoundedYear] = useState('2015');
  const [location, setLocation] = useState('New York, NY');

  // Contact & Links
  const [website, setWebsite] = useState('https://sterlingliterary.com');
  const [twitter, setTwitter] = useState('@SterlingLit');
  const [instagram, setInstagram] = useState('@sterlingliterary');
  const [email, setEmail] = useState('queries@sterlingliterary.com');
  const [phone, setPhone] = useState('(212) 555-0123');

  // Submission Guidelines
  const [submissionGuidelines, setSubmissionGuidelines] = useState(
    `**Query Requirements:**
• Query letter (no more than 1 page)
• Synopsis (1-2 pages)
• First 10 pages of manuscript
• Author bio

**How to Submit:**
Submit your query through our online submission portal at sterlingliterary.com/submit or via email to queries@sterlingliterary.com.

**Response Time:**
We aim to respond to all queries within 6-8 weeks. If you haven't heard from us after 8 weeks, please feel free to follow up or assume a pass.

**What We're Looking For:**
We represent literary fiction, upmarket commercial fiction, suspense/thriller, science fiction, fantasy, memoir, and narrative nonfiction. Please review individual agent MSWLs for specific genre interests.

**What We Don't Represent:**
We do not represent poetry, screenplays, children's picture books, or category romance.

**Additional Notes:**
• Queries only, no phone calls please
• Exclusive submissions not required
• Simultaneous submissions accepted
• Response only if interested`
  );

  const [agencyFocus, setAgencyFocus] = useState(
    `At Sterling Literary Agency, we focus on building long-term partnerships with our authors. Our approach combines:\n\n• Rigorous editorial guidance from query to publication\n• Strategic career planning and positioning\n• Strong industry relationships and negotiation expertise\n• Subsidiary rights management (foreign, film/TV, audio)\n• Author support through every stage of the publishing process\n\nWe represent approximately 150 active clients and close 15-25 new deals annually.`
  );

  const handleSave = () => {
    console.log('Saving agency profile:', {
      agencyName,
      tagline,
      about,
      foundedYear,
      location,
      website,
      twitter,
      instagram,
      email,
      phone,
      submissionGuidelines,
      agencyFocus,
    });
    window.alert('Agency profile saved successfully!');
  };

  const handleLogoUpload = (file: File | null) => {
    if (file) {
      console.log('Uploading logo:', file.name);
      window.alert(`Logo "${file.name}" uploaded successfully!`);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: theme.colors.background,
      fontFamily: theme.fonts.primary,
    }}>
      <Header />

      <main style={{
        maxWidth: '900px',
        margin: '0 auto',
        padding: '60px 40px',
      }}>
        {/* Header */}
        <div style={{ marginBottom: '32px' }}>
          <h1 style={{
            fontSize: '40px',
            fontWeight: '400',
            marginBottom: '8px',
            color: theme.colors.textPrimary,
          }}>
            Agency Profile
          </h1>
          <p style={{
            fontSize: '18px',
            color: theme.colors.textSecondary,
          }}>
            Manage your agency's public profile and submission guidelines
          </p>
        </div>

        {/* Basic Information */}
        <Card style={{ marginBottom: '24px' }}>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '500',
            color: theme.colors.textPrimary,
            marginBottom: '20px',
          }}>
            Basic Information
          </h3>

          <Input
            label="Agency Name *"
            value={agencyName}
            onChange={(e) => setAgencyName(e.target.value)}
            required
            style={{ marginBottom: '16px' }}
          />

          <Input
            label="Tagline"
            value={tagline}
            onChange={(e) => setTagline(e.target.value)}
            placeholder="A brief tagline for your agency"
            helperText="Optional: A short, memorable description of your agency"
            style={{ marginBottom: '16px' }}
          />

          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '16px',
          }}>
            <Input
              label="Founded Year"
              type="number"
              value={foundedYear}
              onChange={(e) => setFoundedYear(e.target.value)}
              placeholder="2015"
            />

            <Input
              label="Location"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="New York, NY"
            />
          </div>
        </Card>

        {/* Logo Upload */}
        <Card style={{ marginBottom: '24px' }}>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '500',
            color: theme.colors.textPrimary,
            marginBottom: '20px',
          }}>
            Agency Logo
          </h3>

          <FileUpload
            label="Upload Logo"
            accept="image/*"
            helperText="PNG, JPG, or SVG. Recommended size: 400x400px"
            onChange={handleLogoUpload}
          />
        </Card>

        {/* About Section */}
        <Card style={{ marginBottom: '24px' }}>
          <Textarea
            label="About the Agency *"
            placeholder="Tell writers about your agency's history, mission, and approach..."
            value={about}
            onChange={(e) => setAbout(e.target.value)}
            rows={10}
            required
            helperText="This will be displayed on your public agency profile"
          />
          <div style={{
            marginTop: '8px',
            fontSize: '13px',
            color: theme.colors.textTertiary,
            textAlign: 'right',
          }}>
            {about.length} characters
          </div>
        </Card>

        {/* Agency Focus */}
        <Card style={{ marginBottom: '24px' }}>
          <Textarea
            label="Agency Focus & Philosophy"
            placeholder="Describe your agency's focus, approach to representation, and what makes you unique..."
            value={agencyFocus}
            onChange={(e) => setAgencyFocus(e.target.value)}
            rows={8}
            helperText="Optional: Share your agency's values and approach to author representation"
          />
        </Card>

        {/* Contact & Links */}
        <Card style={{ marginBottom: '24px' }}>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '500',
            color: theme.colors.textPrimary,
            marginBottom: '20px',
          }}>
            Contact Information
          </h3>

          <Input
            label="Website"
            type="url"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            placeholder="https://yourwebsite.com"
            style={{ marginBottom: '16px' }}
          />

          <Input
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="queries@agency.com"
            style={{ marginBottom: '16px' }}
          />

          <Input
            label="Phone"
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="(555) 123-4567"
            style={{ marginBottom: '16px' }}
          />

          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '16px',
          }}>
            <Input
              label="Twitter Handle"
              value={twitter}
              onChange={(e) => setTwitter(e.target.value)}
              placeholder="@agency"
            />

            <Input
              label="Instagram Handle"
              value={instagram}
              onChange={(e) => setInstagram(e.target.value)}
              placeholder="@agency"
            />
          </div>
        </Card>

        {/* Submission Guidelines */}
        <Card style={{ marginBottom: '24px' }}>
          <Textarea
            label="Submission Guidelines *"
            placeholder="Provide detailed submission guidelines for writers..."
            value={submissionGuidelines}
            onChange={(e) => setSubmissionGuidelines(e.target.value)}
            rows={16}
            required
            helperText="Be clear and specific. Writers will read these carefully before submitting."
          />
        </Card>

        {/* Tips */}
        <Card style={{
          marginBottom: '32px',
          backgroundColor: theme.colors.primaryLight,
          border: `1px solid ${theme.colors.primary}`,
        }}>
          <div style={{
            display: 'flex',
            gap: '12px',
            alignItems: 'flex-start',
          }}>
            <div style={{ fontSize: '20px' }}>💡</div>
            <div>
              <div style={{
                fontSize: '16px',
                fontWeight: '500',
                color: theme.colors.textPrimary,
                marginBottom: '8px',
              }}>
                Profile Tips
              </div>
              <ul style={{
                fontSize: '14px',
                color: theme.colors.textSecondary,
                margin: 0,
                paddingLeft: '20px',
                lineHeight: '1.6',
              }}>
                <li>Keep your submission guidelines clear and up-to-date</li>
                <li>Include realistic response timeframes</li>
                <li>Highlight your agency's unique strengths and successes</li>
                <li>Make sure your website and contact information are current</li>
                <li>Consider adding information about your agency's culture and values</li>
              </ul>
            </div>
          </div>
        </Card>

        {/* Action Buttons */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: '16px',
        }}>
          <Button
            variant="ghost"
            onClick={() => window.location.href = '/agency/dashboard'}
          >
            ← Back to Dashboard
          </Button>
          <div style={{ display: 'flex', gap: '12px' }}>
            <Button
              variant="outline"
              onClick={() => window.alert('Preview functionality')}
            >
              Preview
            </Button>
            <Button
              onClick={handleSave}
              disabled={!agencyName.trim() || !about.trim() || !submissionGuidelines.trim()}
            >
              Save Changes
            </Button>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};
